export class SessionActivityMonitor {
  private static readonly ACTIVITY_DEBOUNCE_MS = 500;

  private timeoutMs: number;
  private warningMs: number;
  private heartbeatIntervalMs: number;

  private onWarning: () => void;
  private onTimeout: () => void;
  private onHeartbeat: () => void;

  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private timeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly activityEvents = [
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
    'click',
  ] as const;

  constructor(opts: {
    /** Total session lifetime in milliseconds */
    timeoutMs: number;
    /** How many ms before timeout to fire onWarning */
    warningMs: number;
    /** How often to call onHeartbeat while the user is active */
    heartbeatIntervalMs: number;
    onWarning: () => void;
    onTimeout: () => void;
    onHeartbeat: () => void;
  }) {
    this.timeoutMs = opts.timeoutMs;
    this.warningMs = opts.warningMs;
    this.heartbeatIntervalMs = opts.heartbeatIntervalMs;
    this.onWarning = opts.onWarning;
    this.onTimeout = opts.onTimeout;
    this.onHeartbeat = opts.onHeartbeat;

    this.attachActivityListeners();
    this.scheduleTimers();
    this.startHeartbeat();
  }

  extend() {
    this.scheduleTimers();
  }

  destroy() {
    this.clearTimers();
    this.stopHeartbeat();
    this.detachActivityListeners();
  }

  private handleActivity = () => {
    // Warning is already shown
    if (this.warningTimer === null) {
      return;
    }

    if (this.debounceTimer !== null) {
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      if (this.warningTimer !== null) {
        this.scheduleTimers();
      }
    }, SessionActivityMonitor.ACTIVITY_DEBOUNCE_MS);
  };

  private attachActivityListeners() {
    this.activityEvents.forEach((ev) =>
      window.addEventListener(ev, this.handleActivity, { passive: true })
    );
  }

  private detachActivityListeners() {
    this.activityEvents.forEach((ev) =>
      window.removeEventListener(ev, this.handleActivity)
    );
  }

  private scheduleTimers() {
    this.clearTimers();

    const warningDelay = this.timeoutMs - this.warningMs;

    // Show warning modal when warningDelay ms of inactivity has passed
    this.warningTimer = setTimeout(() => {
      this.warningTimer = null;
      this.onWarning();
    }, warningDelay);

    // Force logout after timeout
    this.timeoutTimer = setTimeout(() => {
      this.onTimeout();
    }, this.timeoutMs);
  }

  private clearTimers() {
    if (this.warningTimer !== null) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.timeoutTimer !== null) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
  }

  private startHeartbeat() {
    this.heartbeatTimer = setInterval(() => {
      this.onHeartbeat();
    }, this.heartbeatIntervalMs);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
}
